import React, { useEffect, useState } from "react"
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import Breadcrumbs from "components/Common/Breadcrumb"
import {
  saGetTenantDetails,
  saGetTenantSubscription,
  saGetTenantInvoices,
  saGetAllPlans,
  saAssignTenantPlan,
} from "store/actions/superAdminAction"
import { clearTenantDetails } from "store/reducers/superAdminReducer"
import TenantDatabaseMigrationPanel from "./TenantDatabaseMigrationPanel"
import { showAlert } from "store/reducers/alertReducer"
import { ROUTES } from "constants/routes"
import { navigateToAccessibleRoute } from "utils/navigateUnauthorized"
import isPermissionEnabled from "common/isPermissionEnabled"
import { translateBillingCycle, translateStatusLabel } from "utils/statusLabels"
import { getCurrencyLabel } from "utils/currency"

const isPlanInactiveError = message =>
  typeof message === "string" && /inactive/i.test(message)

const getPlanAssignmentFailureMessage = (responsePayload, t) => {
  const apiMessage = responsePayload?.msg || responsePayload?.message
  if (isPlanInactiveError(apiMessage)) {
    return t("The selected plan is inactive. Please choose an active plan.")
  }
  return apiMessage
    ? t(apiMessage)
    : t("Unable to assign the selected plan. Please try again.")
}

const STATUS_COLORS = {
  active: "success",
  suspended: "danger",
  inactive: "secondary",
  pending: "warning",
  cancelled: "secondary",
  expired: "danger",
}

const formatDate = value => {
  if (!value) return "—"
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? "—" : parsed.toLocaleDateString()
}

const formatCurrency = (amount, currency = "SAR") =>
  `${getCurrencyLabel(currency || "SAR")} ${Number(amount || 0).toFixed(2)}`

const formatStatusLabel = value =>
  String(value || "").replace(/_/g, " ").trim().toLowerCase() || "—"

const formatBillingCycle = (t, value) => translateBillingCycle(t, value)

const formatTenantCountries = (tenant, language = "en") => {
  const assignments = tenant?.countryAssignments
  if (Array.isArray(assignments) && assignments.length > 0) {
    const names = assignments
      .map(assignment => {
        const country = assignment?.countryId
        if (!country || typeof country !== "object") return ""
        return language === "sa"
          ? country.nameAr || country.name
          : country.name || country.nameAr
      })
      .filter(Boolean)
    if (names.length > 0) {
      return names.join(", ")
    }
  }

  if (Array.isArray(tenant?.countries) && tenant.countries.length > 0) {
    return tenant.countries
      .map(country =>
        typeof country === "string"
          ? country
          : language === "sa"
            ? country?.nameAr || country?.name
            : country?.name || country?.nameAr
      )
      .filter(Boolean)
      .join(", ")
  }

  return tenant?.country || ""
}

const normalizeTenantStatusLabel = tenant => {
  if (tenant?.statusLabel) return tenant.statusLabel
  if (tenant?.status === 1) return "active"
  if (tenant?.status === 0) return "inactive"
  if (tenant?.status === 3) return "suspended"
  if (tenant?.status === 4) return "pending"
  return String(tenant?.status || "").toLowerCase() || "inactive"
}

const TenantDetails = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()
  document.title = t("Tenant Details | Super Admin")

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const tenantId = new URLSearchParams(location.search).get("id")

  const {
    tenantsLoading,
    tenantActionLoading,
    tenantDetails,
    tenantSubscription,
    tenantInvoices,
    plans,
  } = useSelector(state => state.SuperAdmin)
  const { userRole, accountType, accountScope } = useSelector(state => state.Login)
  const isSuperAdminSession =
    accountScope === "super_admin" || accountType === "superadmin"
  const canViewTenants = isPermissionEnabled(
    userRole,
    104,
    "sa_tenants_1",
    accountType,
    accountScope
  )
  const canEditTenants = isPermissionEnabled(
    userRole,
    104,
    "sa_tenants_3",
    accountType,
    accountScope
  )

  const [assignModal, setAssignModal] = useState(false)
  const [selectedPlanId, setSelectedPlanId] = useState("")
  const [assignmentMode, setAssignmentMode] = useState("comped")
  const [paymentReference, setPaymentReference] = useState("")

  useEffect(() => {
    if (!isSuperAdminSession || !canViewTenants) {
      navigateToAccessibleRoute(navigate)
    }
  }, [isSuperAdminSession, canViewTenants, navigate])

  useEffect(() => {
    if (!tenantId || !canViewTenants) return
    dispatch(saGetTenantDetails({ id: tenantId }))
    dispatch(saGetTenantSubscription({ id: tenantId }))
    dispatch(saGetTenantInvoices({ id: tenantId }))
    dispatch(saGetAllPlans({ status: "active", all: true }))
    return () => {
      dispatch(clearTenantDetails())
    }
  }, [dispatch, tenantId, canViewTenants])

  const handleAssignPlan = () => {
    if (!selectedPlanId) return
    dispatch(
      saAssignTenantPlan({
        id: tenantId,
        planId: selectedPlanId,
        assignmentMode,
        ...(assignmentMode === "manual_paid" && paymentReference
          ? { paymentReference }
          : {}),
      })
    ).then(res => {
        if (res?.payload?.status) {
          const apiMessage = res?.payload?.msg || res?.payload?.message
          dispatch(
            showAlert({
              type: "success",
              message: apiMessage
                ? t(apiMessage)
                : t("Plan assigned successfully"),
            })
          )
          dispatch(saGetTenantSubscription({ id: tenantId }))
          dispatch(saGetTenantInvoices({ id: tenantId }))
          setAssignModal(false)
          setAssignmentMode("comped")
          setPaymentReference("")
        } else {
          dispatch(
            showAlert({
              type: "warning",
              message: getPlanAssignmentFailureMessage(res?.payload, t),
            })
          )
        }
      }
    )
  }

  if (tenantsLoading && !tenantDetails?._id) {
    return (
      <div className="page-content text-center py-5">
        <Spinner color="primary" />
      </div>
    )
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs />
          <Row className="align-items-center mb-3">
            <Col>
              <h4 className="mb-0">
                {tenantDetails.name || t("Tenant Details")}
              </h4>
              <p className="text-muted mb-0">{tenantDetails.domain || ""}</p>
            </Col>
            <Col xs="auto" className="d-flex gap-2">
              {canEditTenants && (
              <>
              <Button
                color="primary"
                outline
                onClick={() =>
                  navigate(`${ROUTES.SA_TENANT_EDIT}?id=${tenantId}`)
                }
              >
                <i className="bx bx-edit me-1" />
                {t("Edit")}
              </Button>
              <Button
                color="success"
                onClick={() => {
                  setAssignmentMode("comped")
                  setPaymentReference("")
                  setAssignModal(true)
                }}
              >
                <i className="bx bx-transfer me-1" />
                {t("Assign Plan")}
              </Button>
              </>
              )}
            </Col>
          </Row>

          <Row>
            {/* Tenant Info */}
            <Col xl={6}>
              <Card>
                <CardBody>
                  <h5 className="card-title mb-3">{t("Tenant Information")}</h5>
                  <table className="table table-borderless mb-0">
                    <tbody>
                      <tr>
                        <th className="text-muted fw-normal" width="40%">
                          {t("Name")}
                        </th>
                        <td>{tenantDetails.name || "—"}</td>
                      </tr>
                      <tr>
                        <th className="text-muted fw-normal">{t("Domain")}</th>
                        <td>{tenantDetails.domain || "—"}</td>
                      </tr>
                      <tr>
                        <th className="text-muted fw-normal">{t("Account ID")}</th>
                        <td>{tenantDetails.accountId || "—"}</td>
                      </tr>
                      <tr>
                        <th className="text-muted fw-normal">
                          {t("Custom Login")}
                        </th>
                        <td>
                          {tenantDetails.useCustomLogin ? t("Enabled") : t("Disabled")}
                        </td>
                      </tr>
                      <tr>
                        <th className="text-muted fw-normal">
                          {t("Customer Website")}
                        </th>
                        <td>
                          {tenantDetails.hasWebsite || tenantDetails.websiteDomain
                            ? t("Enabled")
                            : t("Disabled")}
                        </td>
                      </tr>
                      {(tenantDetails.hasWebsite || tenantDetails.websiteDomain) && (
                        <tr>
                          <th className="text-muted fw-normal">
                            {t("Website Domain")}
                          </th>
                          <td>{tenantDetails.websiteDomain || "—"}</td>
                        </tr>
                      )}
                      <tr>
                        <th className="text-muted fw-normal">
                          {t("QR Website")}
                        </th>
                        <td>
                          {tenantDetails.hasQrWebsite ||
                          tenantDetails.qrWebsiteDomain
                            ? t("Enabled")
                            : t("Disabled")}
                        </td>
                      </tr>
                      {(tenantDetails.hasQrWebsite ||
                        tenantDetails.qrWebsiteDomain) && (
                        <tr>
                          <th className="text-muted fw-normal">
                            {t("QR Website Domain")}
                          </th>
                          <td>{tenantDetails.qrWebsiteDomain || "—"}</td>
                        </tr>
                      )}
                      <tr>
                        <th className="text-muted fw-normal">
                          {t("Customer Android app")}
                        </th>
                        <td>
                          {tenantDetails.hasCustomerAndroidApp
                            ? t("Enabled")
                            : t("Disabled")}
                        </td>
                      </tr>
                      <tr>
                        <th className="text-muted fw-normal">
                          {t("Customer iOS app")}
                        </th>
                        <td>
                          {tenantDetails.hasCustomerIosApp
                            ? t("Enabled")
                            : t("Disabled")}
                        </td>
                      </tr>
                      <tr>
                        <th className="text-muted fw-normal">
                          {t("Rider Android app")}
                        </th>
                        <td>
                          {tenantDetails.hasRiderAndroidApp
                            ? t("Enabled")
                            : t("Disabled")}
                        </td>
                      </tr>
                      <tr>
                        <th className="text-muted fw-normal">
                          {t("Rider iOS app")}
                        </th>
                        <td>
                          {tenantDetails.hasRiderIosApp
                            ? t("Enabled")
                            : t("Disabled")}
                        </td>
                      </tr>
                      <tr>
                        <th className="text-muted fw-normal">
                          {t("Contact Email")}
                        </th>
                        <td>{tenantDetails.contactEmail || "—"}</td>
                      </tr>
                      <tr>
                        <th className="text-muted fw-normal">
                          {t("Tenant Admin Email")}
                        </th>
                        <td>{tenantDetails.adminEmail || "—"}</td>
                      </tr>
                      <tr>
                        <th className="text-muted fw-normal">{t("Order Prefix")}</th>
                        <td>
                          {tenantDetails.orderNumbering?.prefix
                            ? `${tenantDetails.orderNumbering.prefix}${"0".repeat(
                                (tenantDetails.orderNumbering.serialPadding || 13) - 1
                              )}1`
                            : "—"}
                        </td>
                      </tr>
                      <tr>
                        <th className="text-muted fw-normal">
                          {t("Country")}
                        </th>
                        <td>
                          {formatTenantCountries(tenantDetails, language) || "—"}
                        </td>
                      </tr>
                      <tr>
                        <th className="text-muted fw-normal">{t("Status")}</th>
                        <td>
                          <Badge
                            color={
                              STATUS_COLORS[normalizeTenantStatusLabel(tenantDetails)] || "secondary"
                            }
                            className="text-capitalize"
                          >
                            {translateStatusLabel(t, normalizeTenantStatusLabel(tenantDetails))}
                          </Badge>
                        </td>
                      </tr>
                      <tr>
                        <th className="text-muted fw-normal">
                          {t("Created")}
                        </th>
                        <td>
                          {tenantDetails.createdAt
                            ? new Date(
                                tenantDetails.createdAt
                              ).toLocaleDateString()
                            : "—"}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </CardBody>
              </Card>
            </Col>

            {/* Subscription Info */}
            <Col xl={6}>
              <Card>
                <CardBody>
                  <h5 className="card-title mb-3">
                    {t("Current Subscription")}
                  </h5>
                  {tenantSubscription ? (
                    <table className="table table-borderless mb-0">
                      <tbody>
                        <tr>
                          <th className="text-muted fw-normal" width="40%">
                            {t("Plan")}
                          </th>
                          <td>
                            {tenantSubscription.plan?.name ||
                              tenantSubscription.planName ||
                              "—"}
                          </td>
                        </tr>
                        <tr>
                          <th className="text-muted fw-normal">
                            {t("Status")}
                          </th>
                          <td>
                            <Badge
                              color={
                                STATUS_COLORS[formatStatusLabel(tenantSubscription.status)] ||
                                "secondary"
                              }
                              className="text-capitalize"
                            >
                              {translateStatusLabel(t, formatStatusLabel(tenantSubscription.status))}
                            </Badge>
                          </td>
                        </tr>
                        <tr>
                          <th className="text-muted fw-normal">
                            {t("Billing Cycle")}
                          </th>
                          <td>
                            {formatBillingCycle(
                              t,
                              tenantSubscription.plan?.billingCycle ||
                                tenantSubscription.metadata?.billingCycle
                            )}
                          </td>
                        </tr>
                        <tr>
                          <th className="text-muted fw-normal">
                            {t("Started")}
                          </th>
                          <td>{formatDate(tenantSubscription.startedAt)}</td>
                        </tr>
                        <tr>
                          <th className="text-muted fw-normal">
                            {t("Expires")}
                          </th>
                          <td>{formatDate(tenantSubscription.expiresAt)}</td>
                        </tr>
                        <tr>
                          <th className="text-muted fw-normal">
                            {t("Payment Method")}
                          </th>
                          <td className="text-capitalize">
                            {tenantSubscription.paymentMethod || "—"}
                          </td>
                        </tr>
                        <tr>
                          <th className="text-muted fw-normal">
                            {t("Auto Renewal")}
                          </th>
                          <td>
                            {typeof tenantSubscription.autoRenewal === "boolean"
                              ? tenantSubscription.autoRenewal
                                ? t("Enabled")
                                : t("Disabled")
                              : "—"}
                          </td>
                        </tr>
                        <tr>
                          <th className="text-muted fw-normal">
                            {t("Amount")}
                          </th>
                          <td>{formatCurrency(tenantSubscription.amount, tenantSubscription.currency)}</td>
                        </tr>
                        <tr>
                          <th className="text-muted fw-normal">
                            {t("Subscription ID")}
                          </th>
                          <td>{tenantSubscription._id || "—"}</td>
                        </tr>
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-muted">{t("No active subscription.")}</p>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          {tenantDetails?.hasSeparateDb ? (
            <Row>
              <Col xl={12}>
                <TenantDatabaseMigrationPanel
                  tenantId={tenantId}
                  tenantDetails={tenantDetails}
                />
              </Col>
            </Row>
          ) : null}

          {/* Invoice History */}
          <Card>
            <CardBody>
              <h5 className="card-title mb-3">{t("Invoice History")}</h5>
              {tenantInvoices.length === 0 ? (
                <p className="text-muted">{t("No invoices found.")}</p>
              ) : (
                <div className="table-responsive">
                  <Table className="table align-middle table-nowrap mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>{t("Invoice #")}</th>
                        <th>{t("Date")}</th>
                        <th>{t("Due Date")}</th>
                        <th>{t("Paid Date")}</th>
                        <th>{t("Amount")}</th>
                        <th>{t("Status")}</th>
                        <th>{t("Gateway")}</th>
                        <th>{t("Items")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tenantInvoices.map((inv, idx) => (
                        <tr key={inv._id || idx}>
                          <td>{inv.invoiceNumber || inv._id}</td>
                          <td>{formatDate(inv.issueDate || inv.createdAt)}</td>
                          <td>{formatDate(inv.dueDate)}</td>
                          <td>{formatDate(inv.paidDate)}</td>
                          <td>{formatCurrency(inv.amount, inv.currency)}</td>
                          <td>
                            <Badge
                              color={
                                STATUS_COLORS[formatStatusLabel(inv.status)] ||
                                "secondary"
                              }
                              className="text-capitalize"
                            >
                              {translateStatusLabel(t, formatStatusLabel(inv.status))}
                            </Badge>
                          </td>
                          <td className="text-capitalize">
                            {inv.paymentGateway || "—"}
                          </td>
                          <td>{Array.isArray(inv.items) ? inv.items.length : 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        </Container>
      </div>

      {/* Assign Plan Modal */}
      <Modal isOpen={assignModal} toggle={() => setAssignModal(false)}>
        <ModalHeader toggle={() => setAssignModal(false)}>
          {t("Assign Plan")}
        </ModalHeader>
        <ModalBody>
          <p className="text-muted mb-3">
            {t("Select a subscription plan to assign to this tenant.")}
          </p>
          <div className="d-flex flex-column gap-2">
            {plans
              .filter(
                plan => String(plan?.status || "").toLowerCase() === "active"
              )
              .map(plan => (
              <div
                key={plan._id}
                className={`border rounded p-3 cursor-pointer ${
                  selectedPlanId === plan._id
                    ? "border-primary bg-light"
                    : "border-secondary"
                }`}
                style={{ cursor: "pointer" }}
                onClick={() => setSelectedPlanId(plan._id)}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>{plan.name}</strong>
                    {plan.recommended && (
                      <Badge color="success" className="ms-2">
                        {t("Recommended")}
                      </Badge>
                    )}
                    <div className="text-muted small">{plan.description}</div>
                  </div>
                  <div className="text-end">
                    <span className="fw-bold">
                      {getCurrencyLabel(plan.currency || "SAR")}{" "}
                      {Number(plan.price || 0).toFixed(2)}
                    </span>
                    <div className="text-muted small">
                      /{" "}
                      {plan.billingCycle === "annual"
                        ? t("year")
                        : t("month")}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {selectedPlanId && (
            <div className="mt-4 pt-3 border-top">
              <Label className="fw-semibold mb-2">{t("Payment option")}</Label>
              <FormGroup check className="mb-2">
                <Input
                  type="radio"
                  id="assign-mode-comped"
                  name="assignmentMode"
                  checked={assignmentMode === "comped"}
                  onChange={() => setAssignmentMode("comped")}
                />
                <Label check for="assign-mode-comped" className="ms-1">
                  {t("Activate immediately (comped)")}
                </Label>
              </FormGroup>
              <FormGroup check className="mb-2">
                <Input
                  type="radio"
                  id="assign-mode-payment-link"
                  name="assignmentMode"
                  checked={assignmentMode === "payment_link"}
                  onChange={() => setAssignmentMode("payment_link")}
                />
                <Label check for="assign-mode-payment-link" className="ms-1">
                  {t("Send payment link to tenant admin")}
                </Label>
              </FormGroup>
              <FormGroup check className="mb-2">
                <Input
                  type="radio"
                  id="assign-mode-manual-paid"
                  name="assignmentMode"
                  checked={assignmentMode === "manual_paid"}
                  onChange={() => setAssignmentMode("manual_paid")}
                />
                <Label check for="assign-mode-manual-paid" className="ms-1">
                  {t("Mark as manually paid")}
                </Label>
              </FormGroup>
              {assignmentMode === "manual_paid" && (
                <FormGroup className="mt-2">
                  <Label for="payment-reference">{t("Payment reference")}</Label>
                  <Input
                    id="payment-reference"
                    type="text"
                    value={paymentReference}
                    placeholder={t("Bank transfer, receipt #, etc.")}
                    onChange={e => setPaymentReference(e.target.value)}
                  />
                </FormGroup>
              )}
              {assignmentMode === "payment_link" && (
                <p className="text-muted small mb-0 mt-2">
                  {t(
                    "The tenant admin will receive an email with a secure Tap payment link. The subscription activates after successful payment."
                  )}
                </p>
              )}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setAssignModal(false)}>
            {t("Cancel")}
          </Button>
          <Button
            color="primary"
            disabled={!selectedPlanId || tenantActionLoading}
            onClick={handleAssignPlan}
          >
            {tenantActionLoading ? <Spinner size="sm" /> : t("Assign Plan")}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  )
}

export default TenantDetails
